import React, { SetStateAction, useEffect, useRef, useState } from 'react'
import { fetchData, postData } from '../../../services/api';
import apiUrls from '../../../config/apiUrls';
import handleApiResponse from '../../../utils/handleApiResponse';
import IApiResponse from '../../../types/IApiResponse';
import { ModuleSpinner } from "../../../ui/LoadingSpinner/ModuleSpinner";
import { ChemistryData, fetchChemistrySummaryData, TeamResultSummary } from '../../../pages/Chemistry/Utils';
import { useLoading } from '../../../contexts/LoadingContext';
import { ChemistryPlayerPositions, TeamChemistry } from '../../../types/IOption';
import {AddIcCallOutlined, InfoOutlined, PlusOneOutlined } from "@mui/icons-material";
import Badge from "@mui/material/Badge";
import { TeamRolesPlayed } from './TeamRolesPlayed';
import { AttributesTable } from '../../PlayerDetail/Attributes/AttributesTable';
import { useSearch } from '../../../contexts/PlayerSearchContext';
import { useClubSearch } from '../../../contexts/ClubSearchContext';
import LabelText from '../../../ui/LabelText';
import CustomizedTooltips from '../../../ui/Tooltip';
import { useStyles } from '../../../styled-components/PlayerCard.Styles';
import Plus from "../../../assets/icons/Plus.png";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';


interface GameModelProps{
  teamId : number;
  seasonId : number;
  clubName: string;
  seasonIdForAttributes?: number;
}

export interface IlatLongs{
  locationX: number,
  locationY: number
}

export interface IGamemodelData{
  name: string,
  playerId: number[],
  latLongs: IlatLongs[],
  avgY: number,
  avgX: number
}

const TeamPlayingStyle:React.FC<GameModelProps> = ({teamId , seasonId , clubName , seasonIdForAttributes}) => {

    const {startLoading, stopLoading ,loading} = useLoading(); 
    const classes = useStyles(); 
    const {clubCompareId ,teamCompareAttributesData, setTeamCompareAttributesData, setCompareClubLogoUrl , setIsClubCompareId , setCompareClubName} = useClubSearch()
    const [gameModeldata , setGameModelData] = useState<IGamemodelData[]>([])
    const [isChemistryDataNotEnough, setIsChemistryDataNotEnough] = useState<boolean>(false);
    const [positionResults, setPositionResults] = useState<ChemistryPlayerPositions[]>([]);
    const [teamChemistry, setTeamChemistry] = useState<TeamChemistry[]>([]);
    const [overallSFScore, setOverallSFScore] = useState<number>(0);
    const [avgAge, setAvgAge] = useState<number>(0);
    const [attackContribution, setAttackContribution] = useState<number>(0);
    const [ppda, setPPDA] = useState<number>(0);
    const [leagueValues, setLeaguevalues] = useState<TeamResultSummary>({
        leaguePosition: 0,
        matchesWon: 0,
        matchesLost: 0,
        matchesDraw: 0,
        totalGoals:0,
        totalGoalsAgainst:0,
        xGoals:0,
        xGoalAgainst:0,
        coaches:[]
      });
    const [isPPDATolltipOpen, setIsPPDATooltipOpen] = useState(false);
    const PPDATooltipRef = useRef<NodeJS.Timeout | null>(null);
    const toggleLabel = [
        { key: "style", value: "  Style  " },
        { key: "skill", value: "  Skill  " }
      ];
    const [selectedLabel, setSelectedLabel] = useState<string>("style");
    const [teamAttributesData, setTeamAttributesData] = useState();
    const [isTeamAttributesLoading, setIsTeamAttributesLoading] = useState<boolean>(false);
    const [isGameModelLoading, setIsGameModelLoading] = useState<boolean>(false);
    const [isTeamChemstryLoading, setIsTeamChemestryLoading] = useState<boolean>(false);
    const [chemistryData , setChemistryData] = useState<ChemistryData>();

    useEffect(()=>{
      setCompareClubLogoUrl("");
      setIsClubCompareId(0);
      setCompareClubName("");
    },[])

    useEffect(() => {
          setOverallSFScore(
            Math.trunc(
              teamChemistry?.reduce((acc, curr) => acc + curr.score, 0) / teamChemistry?.filter((connection) => connection?.score > 0)?.length
            )
          );
      }, [teamChemistry])
  
    useEffect(() => {
      if(positionResults.length > 0){
        const validAges = positionResults.filter((p) => p.age > 0);
        const avg = validAges.reduce((acc, curr) => acc + curr.age, 0) / validAges.length;
        const fixedAvg = Number(avg.toFixed(2));
        setAvgAge(fixedAvg);
        setAttackContribution(
          Math.trunc(
            positionResults.reduce((acc, curr) => acc + curr.contributionValue, 0) /
            positionResults.filter((player) => player?.playerId > 0).length
          )
        );
        setPPDA(Number(positionResults.reduce((acc, curr) => acc + curr.ppda, 0).toFixed(2)));
      }
    }, [positionResults]);


    useEffect(()=>{
      const fetchGameModelData = async () =>{
        setIsGameModelLoading(true)
        const response: IApiResponse<IGamemodelData[]> = await fetchData(`${apiUrls["GetGameModel"]}?teamId=${teamId}&seasonId=${seasonId}`);
              handleApiResponse(
                response,
                (GameModelData) => {
                  setGameModelData(GameModelData)
                  setIsGameModelLoading(false)
                },
                (error) => {
                  setIsGameModelLoading(false)
                  console.error("Error fetching game model Data:", error);
                }
              );
      }
      const fetchChemistryData = async () => {
        setIsTeamChemestryLoading(true)
        try {
          const response: IApiResponse<ChemistryData> = await postData(`${apiUrls["TeamChemistryNetwork"]}`, {
            seasonId: seasonId,
            teamId: teamId,
            formationId: 0,
            isOverAll: false,
            matchIds: [],
          });
    
          handleApiResponse(
            response,
            (chemistryData) => {
              setChemistryData(chemistryData)
              setIsTeamChemestryLoading(false)
              setPositionResults(chemistryData?.positionResults);
              const adjustedChemistry = chemistryData?.teamChemstriys.map(connection => ({
                ...connection,
                score: connection.score === 100 ? 99 : connection.score
              }));
              setTeamChemistry(adjustedChemistry);
            },
            (error) => {
              setIsTeamChemestryLoading(false)
              console.error("Error fetching Chemistry Data:", error);
              if (error?.message === "No data for current season threshold not reached") {
                setIsChemistryDataNotEnough(true);
              }
            }
          );
        } catch (error) {
          setIsTeamChemestryLoading(false)
          console.error("Unexpected error while fetching Chemistry Data:", error);
        }
      };
      const fetchTeamAttributesData = async (teamId : number , setData : SetStateAction<any> ) => {
        setIsTeamAttributesLoading(true);
        try {
          const response: IApiResponse<any> = await postData(`${apiUrls["GetTeamAttributes"]}`, {
            seasonId: seasonIdForAttributes,
            teamId: teamId,
          });
    
          handleApiResponse(
            response,
            (teamAttributesData) => {
              setIsTeamAttributesLoading(false);
              setData(teamAttributesData);
            },
            (error) => {
              setIsTeamAttributesLoading(false);
              console.error("Error fetching team Attributes Data:", error);
            }
          );
        } catch (error) {
          setIsTeamAttributesLoading(false);
          console.error("Unexpected error while fetching team Attributes Data:", error);
        }
      };
      if(teamId > 0 && seasonId > 0){
        // fetchGameModelData();
        fetchChemistryData()
        fetchChemistrySummaryData(false, teamId, seasonId, [], setLeaguevalues, startLoading, stopLoading);
        fetchTeamAttributesData(teamId, setTeamAttributesData);
      }
      if(clubCompareId > 0 && seasonId > 0){
        fetchTeamAttributesData(clubCompareId, setTeamCompareAttributesData);
      }
    },[teamId,seasonId, clubCompareId])

    const handlePPDATolltipShow = () => {
      PPDATooltipRef.current = setTimeout(() => {
        setIsPPDATooltipOpen(true);
      }, 500);
    };
  
    const handlePPDATolltipHide = () => {
      if (PPDATooltipRef.current) {
        clearTimeout(PPDATooltipRef.current);
      }
      setIsPPDATooltipOpen(false);
    }

  const handleToggleChange = (SelectedTab: any) => {
    setSelectedLabel(SelectedTab.key);
  }

  const nationalityTooltip = leagueValues?.coaches?.length! > 1 ? (
      <div>
        <ul style={{ margin: "0px", paddingLeft: "20px" }}>
          {leagueValues?.coaches?.map((coaches) => (
            <li key={coaches?.coachId!}>
              <div>
                <LabelText className="tooltip-text">{coaches?.coachName!}</LabelText>
                <LabelText className="tooltip-text">{` (matches : ${coaches?.coachedMatches!})`}</LabelText>
              </div>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <>
    <div className='On-pitch-container' style={{position:'relative'}}>
        <div style={{width:'100%',alignSelf:'center',position:'relative'}}>
          <div className="squad-summary-bar-container" style={{width:'auto'}}>
                        <div className="squad-summarybar-container">
                          <div className="squad-chemistry-box">
                            OVERALL SYSTEM FIT SCORE
                            <span>
                              {loading ? "--" : chemistryData?.avgSystemFitScore}{" "}
                            </span>
                          </div>
                          <div className="squad-chemistry-box">
                            OVERALL ATTACK CONTRIBUTION
                            <span>
                              {loading ? "--" :  attackContribution}{" "}
                            </span>
                          </div>
                          <div className="squad-chemistry-box">
                            PPDA
                            {!loading && (
                              <div className="tooltip-icon-container">
                                <InfoOutlined onMouseEnter={handlePPDATolltipShow} onMouseLeave={handlePPDATolltipHide} />
                                {isPPDATolltipOpen && (
                                  <div
                                    className={`${
                                      (ppda  > 0) ? "ppda-tooltip-swap-active" : "ppda-tooltip "
                                    }`}
                                  >
                                    Passes Per Defensive Action (PPDA) is a stat used to gauge pressing intensity. <br /> The lower the score the
                                    better
                                  </div>
                                )}
                              </div>
                            )}
                            <span>
                              {loading ? "--" : ppda}{" "}
                            </span>
                          </div>
                          <div className="squad-chemistry-box">
                            AV. AGE
                            <span>
                              {loading ? "--" : avgAge}{" "}
                            </span>
                          </div>
                          <div className="squad-chemistry-box">
                            <div>
                              <div style={{display:'flex',gap:'20px',position:'absolute'}}>
                                  MANAGER
                                  {leagueValues?.coaches?.length! > 1 && (
                                    <CustomizedTooltips title={nationalityTooltip}>
                                        {/* <img src={Plus} alt="plus-icon" style={{marginTop:'3px'}}/> */}
                                        <AddCircleOutlineIcon/>
                                    </CustomizedTooltips>
                                  )}
                              </div>
                            </div>
                            <span>
                              {loading ? "--" : leagueValues?.coaches[0]?.coachName! || '--'}{" "}
                            </span>
                           
                            
                          </div>
                        </div>
                        <div className="squad-league-detail-container">
                          <div className="squad-league-position">
                            LEAGUE POSITION
                            <span>{loading || !leagueValues?.leaguePosition ? "--" : leagueValues?.leaguePosition}</span>
                          </div>
                          <div className="squad-win-loss-container">
                            <div className="squad-win-loss-box">
                              GOALS
                              <span>{loading || !leagueValues?.totalGoals ? "--" : leagueValues?.totalGoals}</span>
                            </div>
                            <div className="squad-win-loss-box">
                              G/A
                              <span>{loading || !leagueValues?.totalGoalsAgainst ? "--" : leagueValues?.totalGoalsAgainst}</span>
                            </div>
                            <div className="squad-win-loss-box">
                              XG
                              <span>{loading || !leagueValues?.xGoals ? "--" : leagueValues?.xGoals}</span>
                            </div>
                            <div className="squad-win-loss-box">
                              XG/A
                              <span>{loading || !leagueValues?.xGoalAgainst ? "--" : leagueValues?.xGoalAgainst}</span>
                            </div>
                          </div>
                        </div>
                      {isTeamChemstryLoading && <ModuleSpinner />} 

                     </div>
            {/* <Pitch
                currentView='gameModels'
                gameModelData={gameModeldata}
            /> */}
            {isGameModelLoading && <ModuleSpinner />} 

        </div>
        <div className='game-model-toggle-button'>
        {/* <ToggleButton labels={toggleLabel || []} defaultLabelKey={selectedLabel!} onChange={handleToggleChange} /> */}
        </div>
         {selectedLabel === 'style' ? 
         (
          <TeamRolesPlayed playerName={clubName} teamId={Number(teamId)} seasonId={seasonId!} />
         ):(
          <></>
         )}
        
    </div>
    <AttributesTable playerId={teamId} clubName={clubName} ifForTeam={true} attributesData={teamAttributesData} attributeCompareData={teamCompareAttributesData}/>
    {isTeamAttributesLoading && <ModuleSpinner />}
    
    </>
  )
}

export default TeamPlayingStyle